import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, TrendingDown, CheckCircle, Circle, Download, Search, Link, AlertTriangle, ArrowUpCircle, ArrowLeft } from 'lucide-react';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatting';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu';
import SubscriptionPlans from './SubscriptionPlans';
import OnboardingScreen from './OnboardingScreen';
import { useTheme } from '../context/ThemeContext';

export default function AccountSelector({ onAccountSelect, onBack }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [accounts, setAccounts] = useState([]);
  const [linkedAccounts, setLinkedAccounts] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('linkedGoogleAdsAccounts');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [isDemo, setIsDemo] = useState(false);
  const [retryAfter, setRetryAfter] = useState(null);
  const [viewMode, setViewMode] = useState('linked'); // Changed default to 'linked'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('spend'); // 'spend', 'name', 'conversions'
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [hasNoAccounts, setHasNoAccounts] = useState(false);
  const [adSpendStatus, setAdSpendStatus] = useState(null);
  const [mccHierarchy, setMccHierarchy] = useState(null);

  useEffect(() => {
    fetchAccounts();
    syncLinkedAccountsToBackend(); // Sync localStorage to database
    fetchAdSpendStatus(); // Get tier limit info
    
    // Check if we just came from onboarding success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('onboarding') === 'complete') {
      console.log('🎉 Onboarding complete - refreshing accounts');
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Fetch ad spend status from backend
  const fetchAdSpendStatus = async () => {
    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      console.log('💰 Fetching ad spend status...');
      
      const response = await fetch(`${backendUrl}/api/user/ad-spend-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.error('❌ Ad spend status API error:', response.status);
        return;
      }
      
      const data = await response.json();
      console.log('💰 Ad spend status response:', JSON.stringify(data, null, 2));
      
      if (data.success !== false && data.tierLimit !== undefined) {
        setAdSpendStatus(data);
        console.log(`💰 Tier: ${data.currentTierName}, Limit: €${data.tierLimit}, Spend: €${data.currentSpend}, Blocked: ${data.isBlocked}`);
      } else {
        console.warn('⚠️ Ad spend status missing tierLimit:', data);
      }
    } catch (error) {
      console.error('❌ Error fetching ad spend status:', error);
    }
  };

  // Sync linked accounts from localStorage to backend database
  const syncLinkedAccountsToBackend = async () => {
    try {
      const savedLinked = localStorage.getItem('linkedGoogleAdsAccounts');
      const linkedIds = savedLinked ? JSON.parse(savedLinked) : [];
      
      if (linkedIds.length === 0) {
        console.log('📋 No linked accounts in localStorage to sync');
        return;
      }
      
      console.log(`🔄 Syncing ${linkedIds.length} linked accounts to backend...`);
      
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      const response = await fetch(`${backendUrl}/api/auth/google/connections/sync-linked`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ linkedAccountIds: linkedIds })
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log(`✅ Synced ${data.syncedCount} accounts to backend`);
      } else {
        console.error('❌ Failed to sync accounts:', data.error);
      }
    } catch (error) {
      console.error('❌ Error syncing linked accounts:', error);
    }
  };

  const fetchAccounts = async (fetchAll = true) => {
    setLoading(true);
    setError(null);
    setWarning(null);
    setIsDemo(false);
    setHasNoAccounts(false);
    
    try {
      // Always fetch ALL accounts so user can see what's available
      // The linked/unlinked distinction is handled in the UI
      // Include trends=true to show spend trend percentages on the Linked Accounts page
      console.log('🔍 Fetching ALL user accounts from backend...');
      
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/user/accounts?all=true&trends=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      console.log('📋 User account data received:', data);
      
      if (data.success) {
        if (data.accounts && data.accounts.length > 0) {
          console.log(`✅ Loaded ${data.accounts.length} user-specific accounts`);
          setAccounts(data.accounts);
          setError(null);
          setHasNoAccounts(false);
          
          // Store MCC hierarchy for grouped display
          if (data.mccAccounts && data.hierarchy) {
            setMccHierarchy({
              mccAccounts: data.mccAccounts,
              hierarchy: data.hierarchy,
              orphanAccountIds: data.orphanAccountIds || []
            });
          }
          
          // Sync linkedAccounts from backend is_selected state if localStorage is empty
          const savedLinked = localStorage.getItem('linkedGoogleAdsAccounts');
          const currentLinked = savedLinked ? JSON.parse(savedLinked) : [];
          
          if (currentLinked.length === 0) {
            // Fetch which accounts are selected in the database
            try {
              const token = await getToken();
              const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
              const connResp = await fetch(`${backendUrl}/api/auth/google/connections`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const connData = await connResp.json();
              if (connData.success && connData.connections && connData.connections.length > 0) {
                const selectedIds = connData.connections.map(c => c.customer_id);
                console.log(`📋 Loaded ${selectedIds.length} selected accounts from database`);
                localStorage.setItem('linkedGoogleAdsAccounts', JSON.stringify(selectedIds));
                setLinkedAccounts(selectedIds);
              } else {
                console.log('📋 No selected accounts in database - keeping empty');
              }
            } catch (e) {
              console.error('❌ Error loading selected accounts from database:', e);
            }
          }
          
          // Check if this is post-onboarding or no account is selected
          // BUT don't auto-select if user explicitly requested the overview (clicked logo)
          const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
          const showOverview = localStorage.getItem('showAccountOverview');
          const urlParams = new URLSearchParams(window.location.search);
          const isPostOnboarding = urlParams.get('onboarding') === 'complete';
          
          if (showOverview === 'true') {
            console.log('👤 User requested overview - showing all accounts');
            // Don't auto-select, let user choose
          } else if ((!savedAccount || isPostOnboarding) && onAccountSelect) {
            const firstAccount = data.accounts[0];
            if (firstAccount) {
              console.log(`🎯 Auto-selecting first account: ${firstAccount.name}`);
              // Directly call onAccountSelect without setTimeout
              onAccountSelect(firstAccount);
              
              // Also save to localStorage immediately to ensure apiCall works
              localStorage.setItem('selectedGoogleAdsAccount', JSON.stringify(firstAccount));
            }
          }
        } else {
          // User has no linked accounts
          console.warn('⚠️ User has no linked Google Ads accounts');
          setAccounts([]);
          setHasNoAccounts(true);
          setWarning(data.warning || 'No Google Ads accounts linked yet');
        }
        
        // Check if demo data or warnings
        if (data.isDemo) {
          setIsDemo(true);
          setWarning(data.warning || 'Showing demo data');
        } else if (data.warning) {
          setWarning(data.warning);
        }
      } else {
        console.error('❌ Failed to fetch accounts:', data.error);
        setError(data.error || 'Failed to fetch accounts');
        setAccounts([]);
      }
    } catch (err) {
      console.error('❌ Error fetching accounts:', err);
      setError('Failed to connect to backend. Please try again.');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleLinkedAccount = async (accountId, event) => {
    event.stopPropagation();
    
    // Calculate new linked accounts
    const newLinked = linkedAccounts.includes(accountId) 
      ? linkedAccounts.filter(id => id !== accountId)
      : [...linkedAccounts, accountId];
    
    // Update state and localStorage immediately for UI responsiveness
    setLinkedAccounts(newLinked);
    localStorage.setItem('linkedGoogleAdsAccounts', JSON.stringify(newLinked));
    
    // Sync to backend immediately for tier limit enforcement
    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      const response = await fetch(`${backendUrl}/api/auth/google/connections/sync-linked`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ linkedAccountIds: newLinked })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        console.error('❌ Failed to sync account toggle:', data.error);
      } else {
        console.log(`✅ Synced account toggle: ${newLinked.length} accounts linked`);
        // Refresh ad spend status after successful sync
        fetchAdSpendStatus();
      }
    } catch (error) {
      console.error('❌ Error syncing account toggle:', error);
    }
  };

  const getLinkedStats = () => {
    const linked = accounts.filter(acc => linkedAccounts.includes(acc.id));
    const totalSpend = linked.reduce((sum, acc) => sum + acc.spend_30d, 0);
    const totalAllSpend = accounts.reduce((sum, acc) => sum + acc.spend_30d, 0);
    const remainingSpend = totalAllSpend - totalSpend;
    const spendPercentage = totalAllSpend > 0 ? (totalSpend / totalAllSpend) * 100 : 0;
    const accountPercentage = accounts.length > 0 ? (linked.length / accounts.length) * 100 : 0;
    
    return {
      linkedCount: linked.length,
      remainingCount: accounts.length - linked.length,
      totalSpend,
      remainingSpend,
      spendPercentage,
      accountPercentage
    };
  };

  const stats = getLinkedStats();

  // Filter and sort accounts
  let displayedAccounts = viewMode === 'linked' 
    ? accounts.filter(acc => linkedAccounts.includes(acc.id))
    : accounts;

  if (searchTerm) {
    displayedAccounts = displayedAccounts.filter(acc => 
      acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.id.includes(searchTerm)
    );
  }

  displayedAccounts = [...displayedAccounts].sort((a, b) => {
    if (sortBy === 'spend') return b.spend_30d - a.spend_30d;
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        // background inherited from ThemeWrapper
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
        // color inherited from ThemeWrapper
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>⏳</div>
          <div>Loading accounts...</div>
        </div>
      </div>
    );
  }

  // Show onboarding if user has no linked accounts
  if (!loading && hasNoAccounts) {
    return <OnboardingScreen currentStep={1} />;
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        // background inherited from ThemeWrapper
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // color inherited from ThemeWrapper
        padding: '2rem'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ marginBottom: '0.5rem', fontSize: '1.75rem' }}>Error Loading Accounts</h2>
          <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '1.1rem' }}>{error}</p>
          
          {/* Debug info */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'left',
            fontSize: '0.875rem'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#60a5fa' }}>
              Troubleshooting Steps:
            </div>
            <ol style={{ margin: 0, paddingLeft: '1.5rem', color: '#9ca3af' }}>
              <li style={{ marginBottom: '0.25rem' }}>Check if backend is running: <code style={{ color: '#fff', background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/health</code></li>
              <li style={{ marginBottom: '0.25rem' }}>Open browser console (F12) to see detailed logs</li>
              <li style={{ marginBottom: '0.25rem' }}>Verify your MCC account has sub-accounts</li>
              <li style={{ marginBottom: '0.25rem' }}>Check if Google Ads API quota is exceeded (backend logs)</li>
            </ol>
          </div>
          
          <button 
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchAccounts();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              marginRight: '0.5rem'
            }}
          >
            🔄 Retry
          </button>
          
          <button 
            onClick={() => navigate('/settings')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            🔗 Link Accounts in Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      // background inherited from ThemeWrapper
      padding: '2rem'
      // color inherited from ThemeWrapper
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Demo/Warning Banner */}
        {(warning || isDemo) && (
          <div style={{
            background: isDemo ? 'rgba(251, 146, 60, 0.1)' : 'rgba(59, 130, 246, 0.1)',
            border: `1px solid ${isDemo ? 'rgba(251, 146, 60, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
            borderRadius: '12px',
            padding: '1rem 1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{ fontSize: '2rem' }}>
              {isDemo ? '⚠️' : 'ℹ️'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '1rem', 
                fontWeight: '600', 
                marginBottom: '0.25rem',
                color: isDemo ? '#fb923c' : '#60a5fa'
              }}>
                {isDemo ? 'Demo Mode Active' : 'Notice'}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#d1d5db' }}>
                {warning}
                {retryAfter && (
                  <span style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af' }}>
                    ⏱️ Estimated wait time: {Math.ceil(retryAfter / 60)} minutes
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setLoading(true);
                fetchAccounts();
              }}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
            >
              🔄 Retry
            </button>
          </div>
        )}

        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0 }}>
            Linked Accounts {isDemo && <span style={{ fontSize: '1rem', color: '#fb923c', fontWeight: '400' }}>(Demo Data)</span>}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setViewMode('linked')}
              style={{
                padding: '0.75rem 1.5rem',
                background: viewMode === 'linked' ? '#3b82f6' : theme.colors.bg.secondary,
                color: viewMode === 'linked' ? 'white' : theme.colors.text.secondary,
                border: '1px solid ' + (viewMode === 'linked' ? '#3b82f6' : theme.colors.border.primary),
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <Link size={18} />
              Linked Accounts
            </button>
            <button
              onClick={() => setViewMode('all')}
              style={{
                padding: '0.75rem 1.5rem',
                background: viewMode === 'all' ? '#8b5cf6' : theme.colors.bg.secondary,
                color: viewMode === 'all' ? 'white' : theme.colors.text.secondary,
                border: '1px solid ' + (viewMode === 'all' ? '#8b5cf6' : theme.colors.border.primary),
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <Building2 size={18} />
              All Accounts
            </button>
            <UserMenu onShowSubscription={() => setShowSubscriptionModal(true)} />
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.5rem',
          marginBottom: '2.5rem'
        }}>
          {/* Linked Accounts Card */}
          <div style={{
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ 
                fontSize: '2rem', 
                fontWeight: '700',
                color: theme.colors.text.primary,
                marginBottom: '0.25rem'
              }}>
                {stats.linkedCount} Linked Accounts
              </div>
              <div style={{ fontSize: '0.875rem', color: theme.colors.text.tertiary }}>
                {stats.remainingCount} Accounts Remaining
              </div>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: theme.colors.bg.tertiary,
              borderRadius: '4px',
              overflow: 'hidden',
              marginTop: '1rem'
            }}>
              <div style={{
                height: '100%',
                width: `${stats.accountPercentage}%`,
                background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                borderRadius: '4px',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* Total Spend Card - with tier limit */}
          <div style={{
            background: adSpendStatus?.isBlocked 
              ? 'rgba(239, 68, 68, 0.1)' 
              : theme.colors.bg.secondary,
            border: adSpendStatus?.isBlocked 
              ? '1px solid rgba(239, 68, 68, 0.3)' 
              : `1px solid ${theme.colors.border.primary}`,
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ marginBottom: '0.75rem' }}>
              {(() => {
                // Enterprise has no limit (tierLimit = null)
                const isEnterprise = adSpendStatus?.currentTier === 'enterprise' || adSpendStatus?.tierLimit === null;
                const tierLimit = adSpendStatus?.tierLimit;
                const tierName = adSpendStatus?.currentTierName || 'Loading...';
                const isOverLimit = !isEnterprise && tierLimit && stats.totalSpend > tierLimit;
                
                return (
                  <>
                    <div style={{ 
                      fontSize: '2rem', 
                      fontWeight: '700',
                      color: isOverLimit ? '#ef4444' : theme.colors.text.primary,
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      {isOverLimit && <AlertTriangle size={28} />}
                      {formatCurrency(stats.totalSpend)} Spend/mo
                    </div>
                    <div style={{ fontSize: '0.875rem', color: isOverLimit ? '#fca5a5' : theme.colors.text.tertiary }}>
                      {isEnterprise 
                        ? `Unlimited (${tierName})`
                        : tierLimit 
                          ? `Limit: ${formatCurrency(tierLimit)} (${tierName})`
                          : 'Loading tier info...'
                      }
                    </div>
                  </>
                );
              })()}
            </div>
            
            {/* Progress bar - uses local spend for instant updates */}
            {(() => {
              const isEnterprise = adSpendStatus?.currentTier === 'enterprise' || adSpendStatus?.tierLimit === null;
              
              // Enterprise: no limit, always show green full bar
              if (isEnterprise) {
                return (
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: theme.colors.bg.tertiary,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      height: '100%',
                      width: '100%',
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                      borderRadius: '4px'
                    }} />
                  </div>
                );
              }
              
              const tierLimit = adSpendStatus?.tierLimit || 25000;
              const spendPercentage = (stats.totalSpend / tierLimit) * 100;
              const isOverLimit = stats.totalSpend > tierLimit;
              const isApproachingLimit = spendPercentage > 80 && !isOverLimit;
              
              return (
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: theme.colors.bg.tertiary,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginTop: '1rem'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(spendPercentage, 100)}%`,
                    background: isOverLimit 
                      ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                      : isApproachingLimit 
                        ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                        : 'linear-gradient(90deg, #10b981, #059669)',
                    borderRadius: '4px',
                    transition: 'width 0.3s'
                  }} />
                </div>
              );
            })()}
            
            {/* Upgrade prompt and warnings - uses local spend for instant updates */}
            {(() => {
              const isEnterprise = adSpendStatus?.currentTier === 'enterprise' || adSpendStatus?.tierLimit === null;
              
              // Enterprise has no limits - never show warnings
              if (isEnterprise) {
                return null;
              }
              
              const tierLimit = adSpendStatus?.tierLimit || 25000;
              const spendPercentage = (stats.totalSpend / tierLimit) * 100;
              const isOverLimit = stats.totalSpend > tierLimit;
              const isApproachingLimit = spendPercentage > 80 && !isOverLimit;
              
              // Determine required tier based on spend
              const getRequiredTier = (spend) => {
                if (spend <= 25000) return { name: 'Starter', tier: 'starter' };
                if (spend <= 100000) return { name: 'Growth', tier: 'growth' };
                if (spend <= 250000) return { name: 'Scale', tier: 'scale' };
                return { name: 'Enterprise', tier: 'enterprise' };
              };
              const requiredTier = getRequiredTier(stats.totalSpend);
              
              return (
                <>
                  {isOverLimit && (
                    <button
                      onClick={() => setShowSubscriptionModal(true)}
                      style={{
                        width: '100%',
                        marginTop: '1rem',
                        padding: '0.75rem 1rem',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <ArrowUpCircle size={18} />
                      Upgrade to {requiredTier.name}
                    </button>
                  )}
                  
                  {isApproachingLimit && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      color: '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <AlertTriangle size={14} />
                      Approaching limit ({spendPercentage.toFixed(0)}%)
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Account Centre Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  background: theme.colors.bg.secondary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: '8px',
                  color: theme.colors.text.secondary,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.colors.bg.tertiary;
                  e.currentTarget.style.color = theme.colors.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.colors.bg.secondary;
                  e.currentTarget.style.color = theme.colors.text.secondary;
                }}
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </button>
            )}
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', margin: 0 }}>
              Account Centre
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search 
                size={18} 
                color={theme.colors.text.tertiary}
                style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)'
                }} 
              />
              <input
                type="text"
                placeholder="Search Accounts"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '0.5rem 1rem 0.5rem 2.5rem',
                  background: theme.colors.bg.secondary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: '8px',
                  color: theme.colors.text.primary,
                  fontSize: '0.875rem',
                  width: '250px',
                  outline: 'none'
                }}
              />
            </div>
            
            {/* Sort By */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: theme.colors.text.tertiary }}>Sort By:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  background: theme.colors.bg.secondary,
                  border: `1px solid ${theme.colors.border.primary}`,
                  borderRadius: '8px',
                  color: theme.colors.text.primary,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="spend">Spend</option>
                <option value="name">Account Name</option>
              </select>
            </div>
          </div>
        </div>

        {/* Accounts List */}
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {(() => {
            // Build render list: if we have MCC hierarchy, insert headers
            const renderList = [];
            
            if (mccHierarchy && mccHierarchy.mccAccounts.length > 0) {
              const accountsInMcc = new Set();
              
              for (const mcc of mccHierarchy.mccAccounts) {
                const childIds = mccHierarchy.hierarchy[mcc.id]?.children || [];
                const mccChildren = displayedAccounts.filter(acc => childIds.includes(acc.id));
                
                if (mccChildren.length > 0) {
                  renderList.push({ type: 'mcc-header', mcc, childCount: mccChildren.length });
                  for (const child of mccChildren) {
                    renderList.push({ type: 'account', account: child });
                    accountsInMcc.add(child.id);
                  }
                }
              }
              
              const orphans = displayedAccounts.filter(acc => !accountsInMcc.has(acc.id));
              if (orphans.length > 0 && renderList.length > 0) {
                renderList.push({ type: 'mcc-header', mcc: { name: 'Direct Accounts', id: 'direct' }, childCount: orphans.length });
              }
              for (const orphan of orphans) {
                renderList.push({ type: 'account', account: orphan });
              }
            }
            
            // Fallback to flat list if no hierarchy
            if (renderList.length === 0) {
              for (const account of displayedAccounts) {
                renderList.push({ type: 'account', account });
              }
            }
            
            return renderList.map((item, idx) => {
              if (item.type === 'mcc-header') {
                return (
                  <div key={`mcc-${item.mcc.id}`} style={{
                    padding: '0.75rem 2rem',
                    background: 'rgba(59, 130, 246, 0.08)',
                    borderBottom: `1px solid ${theme.colors.border.secondary}`,
                    borderTop: idx > 0 ? '1px solid rgba(59, 130, 246, 0.15)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <Building2 size={14} color="#3b82f6" />
                    <span style={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: theme.mode === 'light' ? '#2563eb' : '#60a5fa',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {item.mcc.name}
                    </span>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      color: theme.colors.text.muted,
                      marginLeft: 'auto'
                    }}>
                      {item.childCount} account{item.childCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                );
              }
              
              const account = item.account;
              const index = idx;
            const isLinked = linkedAccounts.includes(account.id);
            // Use real trend data from API (compared to previous 30 days)
            const spendTrend = account.spend_trend || 0;
            const trendPositive = spendTrend >= 0;
            const trendPercent = Math.abs(spendTrend).toFixed(1);
            // Use real conversions from API, fallback to 0
            const conversions = account.conversions_30d || 0;
            
            // SECURITY: Only allow selecting linked accounts
            const handleAccountClick = () => {
              if (!isLinked) {
                // Show alert - user must link the account first
                alert(`⚠️ Account "${account.name}" is not linked.\n\nTo access this account, you must first link it by clicking the checkbox.\n\nNote: Linking more accounts may affect your subscription tier based on total ad spend.`);
                return;
              }
              onAccountSelect(account);
            };
            
            return (
              <div
                key={account.id}
                onClick={handleAccountClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1.5rem 2rem',
                  borderBottom: index < displayedAccounts.length - 1 ? `1px solid ${theme.colors.border.secondary}` : 'none',
                  cursor: isLinked ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s',
                  background: 'transparent',
                  opacity: isLinked ? 1 : 0.5
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isLinked ? theme.colors.bg.hover : 'rgba(255, 100, 100, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {/* Checkbox + Avatar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '0 0 280px' }}>
                  <div onClick={(e) => toggleLinkedAccount(account.id, e)}>
                    {isLinked ? (
                      <CheckCircle size={20} style={{ color: '#3b82f6', cursor: 'pointer' }} />
                    ) : (
                      <Circle size={20} style={{ color: '#6b7280', cursor: 'pointer' }} />
                    )}
                  </div>
                  
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: `linear-gradient(135deg, ${account.name.charCodeAt(0) % 2 === 0 ? '#3b82f6' : '#8b5cf6'}, ${account.name.charCodeAt(0) % 2 === 0 ? '#2563eb' : '#7c3aed'})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {account.name.substring(0, 2).toUpperCase()}
                  </div>
                  
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.text.primary }}>
                      {account.name}
                      {account.isDemo && (
                        <span style={{
                          fontSize: '0.7rem',
                          background: 'rgba(251, 146, 60, 0.2)',
                          color: '#fb923c',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: '700',
                          border: '1px solid rgba(251, 146, 60, 0.3)'
                        }}>
                          DEMO
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginTop: '2px' }}>
                      {account.isDemo ? 'Sample data for testing' : (
                        <a 
                          href={`https://ads.google.com/aw/campaigns?ocid=${account.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: '#60a5fa',
                            textDecoration: 'none',
                            fontSize: '0.7rem'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          🔗 View in Google Ads →
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Spend (30d) */}
                <div style={{ flex: '0 0 200px', paddingLeft: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginBottom: '4px' }}>
                    Spend (30d)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '1.125rem', fontWeight: '700', color: theme.colors.text.primary }}>
                      {formatCurrency(account.spend_30d)}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      color: trendPositive ? '#10b981' : '#ef4444',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {trendPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      {trendPercent}%
                    </div>
                  </div>
                </div>

                {/* Mini Trend Chart - based on real trend direction */}
                <div style={{ flex: '0 0 120px', height: '40px', paddingLeft: '1rem' }}>
                  <svg width="100%" height="40" style={{ display: 'block' }}>
                    {/* Simple trend line: upward for positive, downward for negative, flat for ~0 */}
                    <polyline
                      fill="none"
                      stroke={trendPositive ? '#10b981' : '#ef4444'}
                      strokeWidth="2"
                      strokeLinecap="round"
                      points={
                        Math.abs(spendTrend) < 2 
                          ? '0,20 25,20 50,20 75,20 100,20' // Flat trend (< 2%)
                          : trendPositive 
                            ? '0,32 25,26 50,20 75,14 100,8' // Upward trend
                            : '0,8 25,14 50,20 75,26 100,32' // Downward trend
                      }
                    />
                  </svg>
                </div>

                {/* Conversions - Real data from API */}
                <div style={{ flex: '0 0 180px', paddingLeft: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginBottom: '4px' }}>
                    Conv. (30d)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: '1.125rem', fontWeight: '700', color: theme.colors.text.primary }}>
                      {formatNumber(conversions, 0)}
                    </div>
                  </div>
                </div>

                {/* Active Campaigns */}
                <div style={{ flex: '0 0 100px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.tertiary, marginBottom: '4px' }}>
                    Active
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: theme.colors.text.primary }}>
                    {account.activeCampaigns || '—'}
                  </div>
                </div>

                {/* Settings Icon Placeholder */}
                <div style={{ flex: '0 0 40px', textAlign: 'right' }}>
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '6px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      background: '#3b82f6',
                      marginRight: '2px'
                    }} />
                  </div>
                </div>
              </div>
            );
            });
          })()}
        </div>

        {/* Empty State */}
        {displayedAccounts.length === 0 && (
          <div style={{
            padding: '4rem',
            textAlign: 'center',
            color: theme.colors.text.tertiary
          }}>
            <Building2 size={48} color={theme.colors.text.muted} style={{ margin: '0 auto 1rem' }} />
            <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: theme.colors.text.secondary }}>
              No accounts found
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              {searchTerm ? 'Try adjusting your search' : 'No linked accounts yet'}
            </div>
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionPlans onClose={() => setShowSubscriptionModal(false)} />
      )}
    </div>
  );
}
